import { useEffect, useRef } from "react";

/**
 * Visibility-aware polling.
 *
 * Calls `callback` every `intervalMs` while `enabled` is true AND the browser
 * tab is visible. When the tab is hidden the interval is cleared, and when it
 * becomes visible again `callback` fires once immediately and polling resumes.
 *
 * Pausing on hidden tabs matters for cost: a forgotten background tab that
 * keeps hitting DB-backed endpoints keeps the Neon compute awake (it can't
 * scale to zero while queries arrive). This stops that.
 */
export function usePolling(
  callback: () => void,
  intervalMs: number,
  enabled = true
): void {
  const savedCallback = useRef(callback);
  savedCallback.current = callback;

  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setInterval> | null = null;

    const startInterval = () => {
      if (timer == null) {
        timer = setInterval(() => savedCallback.current(), intervalMs);
      }
    };
    const stopInterval = () => {
      if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        // Tab regained focus — refresh immediately, then resume polling.
        savedCallback.current();
        startInterval();
      }
    };

    if (!document.hidden) startInterval();
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      stopInterval();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, intervalMs]);
}

export default usePolling;
